import { useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '@/i18n/runtime';
import type { TenantConfig } from '@/types';
import {
  type ArrivalBeforeWindow,
  type ArrivalWindowClosed,
  formatCountdown,
  msUntil,
} from '../arrivalWindow';
import { customerTimeZone, slotTimeLines } from '../slotTime';
import { AppBar } from '../components/AppBar';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { Stepper } from '../components/Stepper';

/**
 * CD32 · the two scheduled-arrival screens — SCREENS, not error states.
 *
 * <p>A customer who booked 12:30 and opens their link at 11:34 has done nothing
 * wrong; a customer who opens it at 14:00 has missed an appointment, which is a
 * normal human event with a normal remedy. Neither is an error to be reported at
 * them: each gets a first-class screen with the tenant's own chrome, their
 * appointment time in their own words, and a way forward.
 *
 * <p><strong>Nothing is reset.</strong> These replace only the waiting-room
 * STAGE. Consent, capture and every completed check stay exactly as they were —
 * the stepper is rendered precisely so the customer can see that — and the
 * reschedule affordance is the waiting room's own, handed down, never a second
 * implementation of the same act.
 */

interface CommonProps {
  readonly tenant: TenantConfig;
  /** The waiting room's own "Reschedule for later" handler — one entry point, shared. */
  readonly onReschedule: () => void;
  readonly rescheduling: boolean;
}

export interface ScheduledArrivalProps extends CommonProps {
  readonly refusal: ArrivalBeforeWindow;
  /** Fired EXACTLY ONCE, when the countdown reaches zero: retry queue entry. */
  readonly onWindowOpen: () => void;
}

/**
 * BEFORE the window. Counts down to `windowOpensAt` and, at zero, retries queue
 * entry ONCE — after which the normal flow takes over (a success shows the real
 * queue; a fresh refusal re-renders this screen against the be's new answer).
 * The single retry is latched per countdown target, so a clock-skewed refusal
 * cannot turn this screen into a retry loop against the backend.
 */
export function ScheduledArrival({
  tenant,
  refusal,
  onWindowOpen,
  onReschedule,
  rescheduling,
}: ScheduledArrivalProps): JSX.Element {
  const { t, locale } = useT();
  const timeZone = useMemo(() => customerTimeZone(), []);
  const lines = slotTimeLines(refusal.slotStart ?? refusal.windowOpensAt, locale, timeZone);
  const [remainingMs, setRemainingMs] = useState(() => msUntil(refusal.windowOpensAt, Date.now()));

  // The latch lives in a ref keyed by the target instant: the effect re-arms only
  // when the be names a genuinely different opening time.
  const onWindowOpenRef = useRef(onWindowOpen);
  onWindowOpenRef.current = onWindowOpen;
  const target = refusal.windowOpensAt;
  useEffect(() => {
    let fired = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    const stop = (): void => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const tick = (): void => {
      const left = msUntil(target, Date.now());
      setRemainingMs(left);
      if (left > 0 || fired) return;
      fired = true;
      stop();
      onWindowOpenRef.current();
    };
    timer = setInterval(tick, 1000);
    tick(); // a refusal that arrives already at zero (clock skew) retries at once
    return stop;
  }, [target]);

  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle={t('droom.arrival.subtitle')} />
      <Stepper current="waiting" />

      <div className="wait-hero">
        <span className="wait-ring r1" />
        <span className="wait-ring r2" />
        <span className="wait-ring r3" />
        <span className="wait-core">
          <Icon name="clock" size={38} />
        </span>
      </div>

      <section className="card stack" style={{ padding: 16, marginTop: 16 }}>
        <h2>{t('droom.arrival.title')}</h2>
        {lines ? (
          <>
            <p className="h-title">{lines.local}</p>
            {lines.ist ? <p className="sub">{t('droom.arrival.ist', { time: lines.ist })}</p> : null}
          </>
        ) : null}
        <p className="sub">{t('droom.arrival.body')}</p>
        <div className="eta mono" role="timer" aria-label={t('droom.arrival.countdownAria')}>
          <Icon name="clock" size={14} />
          {t('droom.arrival.countdown', { time: formatCountdown(remainingMs) })}
        </div>
        {remainingMs <= 0 ? (
          /* The automatic retry has fired and is spent (once, by design — a latch, never a
             loop). If the be refused it again, the customer's device clock is simply ahead of
             the server's: a manual retry is then the only thing standing between them and the
             queue, so it is offered rather than leaving a countdown frozen at 00:00. */
          <Button variant="ghost" onClick={onWindowOpen} type="button">
            {t('droom.action.tryAgain')}
          </Button>
        ) : null}
      </section>

      <div className="reassure-row" style={{ marginTop: 16 }}>
        <div className="reassure">
          <div className="ic">
            <Icon name="monitor" size={17} />
          </div>
          {t('droom.wait.keepOpen')}
        </div>
        <div className="reassure">
          <div className="ic">
            <Icon name="autoconnect" size={17} />
          </div>
          {t('droom.arrival.autoconnect')}
        </div>
        <div className="reassure">
          <div className="ic">
            <Icon name="shield" size={17} />
          </div>
          {t('droom.arrival.stepsSaved')}
        </div>
      </div>

      <div className="spacer" />

      <div className="stack-sm" style={{ marginTop: 14 }}>
        <Button variant="soft" onClick={onReschedule} disabled={rescheduling} type="button">
          {rescheduling ? t('droom.wait.rescheduling') : t('droom.wait.reschedule')}
        </Button>
      </div>
    </div>
  );
}

export interface MissedWindowProps extends CommonProps {
  readonly refusal: ArrivalWindowClosed;
}

/**
 * AFTER the window closed. The remedy is prominent and singular — pick a new
 * time — and the copy says plainly that a new time is needed, because a screen
 * that hedged here would leave the customer waiting for a call that will never
 * come.
 */
export function MissedWindow({
  tenant,
  refusal,
  onReschedule,
  rescheduling,
}: MissedWindowProps): JSX.Element {
  const { t, locale } = useT();
  const timeZone = useMemo(() => customerTimeZone(), []);
  const lines = refusal.slotStart ? slotTimeLines(refusal.slotStart, locale, timeZone) : null;

  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle={t('droom.arrival.subtitle')} />
      <Stepper current="waiting" />

      <div className="wait-hero">
        <span className="wait-core">
          <Icon name="clock" size={38} />
        </span>
      </div>

      <section className="card stack" style={{ padding: 16, marginTop: 16 }} role="status">
        <h2>{t('droom.arrival.missed.title')}</h2>
        {lines ? (
          <>
            <p className="sub">{t('droom.arrival.missed.was', { time: lines.local })}</p>
            {lines.ist ? <p className="sub">{t('droom.arrival.ist', { time: lines.ist })}</p> : null}
          </>
        ) : null}
        <p className="sub">{t('droom.arrival.missed.body')}</p>
        <Button onClick={onReschedule} disabled={rescheduling} type="button">
          {rescheduling ? t('droom.wait.rescheduling') : t('droom.arrival.missed.cta')}
        </Button>
      </section>

      <div className="spacer" />

      <div className="reassure-row" style={{ marginTop: 16 }}>
        <div className="reassure">
          <div className="ic">
            <Icon name="shield" size={17} />
          </div>
          {t('droom.arrival.stepsSaved')}
        </div>
      </div>
    </div>
  );
}
